'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Eyebrow, TwitchIcon, YouTubeIcon, TikTokIcon, ShooshMark } from './ui';

/* ─────────────────────────────────────────────────────────────────────────
   NAV
   ──────────────────────────────────────────────────────────────────────── */
export function Nav() {
  return (
    <nav className="sticky top-0 z-40 border-b border-gum-gray-800 bg-gum-gray-950/85 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link href="#hero" className="flex items-center">
          <ShooshMark height={28} />
        </Link>
        <div className="hidden md:flex items-center gap-8 text-sm font-semibold text-gum-gray-300">
          <a href="#features" className="hover:text-gum-pink transition-colors">
            Features
          </a>
          <a href="#streamelements" className="hover:text-gum-pink transition-colors">
            Switch from SE
          </a>
          <a href="#pricing" className="hover:text-gum-pink transition-colors">
            Pricing
          </a>
          <a href="#faq" className="hover:text-gum-pink transition-colors">
            FAQ
          </a>
        </div>
        <div className="flex items-center gap-4">
          <Link href="/login" className="text-sm font-semibold text-gum-gray-300 hover:text-gum-pink transition-colors">
            Log in
          </Link>
          <Link
            href="/login?mode=signup"
            className="inline-flex items-center justify-center font-bold rounded-lg px-5 py-2.5 text-sm bg-gum-pink text-black hover:brightness-110 active:scale-[0.98] transition-all"
          >
            Start Free Trial
          </Link>
        </div>
      </div>
    </nav>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   HERO — slot machine
   ──────────────────────────────────────────────────────────────────────── */
const SLOT_WORDS = [
  'Stream alerts',
  'TTS engine',
  'chat arcade',
  'tip jar',
  'hype squad',
  'links hub',
  'soundboard',
  'command center',
];
const SLOT_INTERVAL_MS = 1900;

export function Hero() {
  const [i, setI] = useState(0);

  useEffect(() => {
    // No cycling/animation for reduced-motion users — leave the first word static.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setI((p) => (p + 1) % SLOT_WORDS.length), SLOT_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="hero" className="relative overflow-hidden">
      {/* soft pink glow behind the headline */}
      <div
        className="pointer-events-none absolute left-1/2 top-24 h-[420px] w-[680px] max-w-[90vw] -translate-x-1/2 rounded-full opacity-60"
        style={{ background: 'radial-gradient(circle, rgba(255,144,232,0.18) 0%, rgba(255,144,232,0) 70%)' }}
      />
      <div className="relative max-w-4xl mx-auto px-6 pt-20 pb-24 md:pt-28 md:pb-28 text-center">
        <h1 className="font-black tracking-tight leading-[1.04]">
          <span className="block text-3xl sm:text-4xl md:text-5xl text-white/85">Shoosh is your</span>
          <span className="mt-1 flex justify-center text-5xl sm:text-6xl md:text-8xl">
            <span className="slot">
              <span key={i} className="slot-word gradient-text">
                {SLOT_WORDS[i]}
              </span>
            </span>
          </span>
        </h1>

        <p className="mt-8 text-base md:text-lg text-gum-gray-400 max-w-2xl mx-auto leading-relaxed">
          The all-in-one stream toolkit for Twitch, YouTube &amp; TikTok — TTS, alerts, donations, sounds, chat games,
          and your links hub, run from one browser dashboard that goes quiet the moment you talk.
        </p>

        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link
            href="/login?mode=signup"
            className="inline-flex flex-col items-center justify-center font-bold rounded-lg px-7 py-3.5 text-lg bg-gum-pink text-black hover:brightness-110 active:scale-[0.98] transition-all"
          >
            Start Free Trial
            <span className="text-xs font-semibold mt-0.5 opacity-80">7 days, no credit card</span>
          </Link>
          <a
            href="#command-center"
            className="inline-flex items-center justify-center font-bold rounded-lg px-7 py-3.5 text-lg border-2 border-gum-gray-700 text-white hover:border-gum-pink hover:text-gum-pink transition-colors"
          >
            See everything ↓
          </a>
        </div>

        <div className="mt-14 flex items-center justify-center gap-6">
          <TwitchIcon size={30} className="float-icon" />
          <YouTubeIcon size={30} className="float-icon" />
          <TikTokIcon size={30} className="float-icon" />
        </div>
        <p className="mt-5 text-sm text-gum-gray-500">No install · Cloud-hosted · Set up in 5 minutes</p>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   COMMAND CENTER — a shrunk, squarer rendition of the real dashboard
   (CSS, not a screenshot) — same pink-outline + glow treatment.
   ──────────────────────────────────────────────────────────────────────── */
function DBtn({ children, active }) {
  return (
    <span
      className={`whitespace-nowrap rounded border px-1.5 py-0.5 ${
        active ? 'border-gum-pink/60 text-gum-pink' : 'border-gum-gray-700 text-gum-gray-300'
      }`}
    >
      {children}
    </span>
  );
}

function WaveBars({ h = 12, color = '#ff90e8' }) {
  return (
    <span className="flex items-end gap-0.5" style={{ height: h }}>
      {[0, 1, 2, 3, 4, 5].map((b) => (
        <span
          key={b}
          className="w-0.5 rounded-full"
          style={{
            height: h,
            background: color,
            transformOrigin: 'bottom',
            animation: `micWave 0.8s ease-in-out ${b * 0.09}s infinite`,
          }}
        />
      ))}
    </span>
  );
}

function MixerFader({ icon, label, sub, pct }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[9px]">
        <span className="flex items-center gap-1 font-semibold text-gum-gray-200">
          <span className="text-gum-gray-400">{icon}</span>
          {label}
        </span>
        <span className="font-mono text-gum-gray-400">{pct}%</span>
      </div>
      <div className="relative my-1 h-1 rounded-full bg-gum-gray-800">
        <div className="absolute inset-y-0 left-0 rounded-full bg-gum-pink" style={{ width: `${pct}%` }} />
        <div
          className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow"
          style={{ left: `${pct}%` }}
        />
      </div>
      <div className="text-[7px] uppercase tracking-wide text-gum-gray-600">{sub}</div>
    </div>
  );
}

function RailHeader({ title }) {
  return (
    <div className="mb-1.5 flex items-center justify-between">
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-0.5 rounded-full bg-gum-pink" />
        <span className="text-[8px] font-bold uppercase tracking-[0.12em] text-gum-gray-200">{title}</span>
      </span>
      <span className="flex items-center gap-1 text-[7px] font-semibold text-emerald-400">
        <span className="h-1 w-1 rounded-full bg-emerald-400" />
        live
      </span>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="rounded border border-gum-gray-800 bg-black/40 px-1.5 py-1">
      <div className="text-[6.5px] uppercase tracking-wide text-gum-gray-600">{label}</div>
      <div className="font-mono text-[10px] font-bold leading-tight text-gum-pink">{value}</div>
    </div>
  );
}

const PLATFORM_META = [
  ['Twitch', '#9146ff', 'twitch'],
  ['YouTube', '#ff0000', 'youtube'],
  ['TikTok', '#ee1d52', 'tiktok'],
];
// Activity feed — alert events [glyph, chip color, text]; freshness shown by row position (STAMP_LADDER).
const DASH_ACTIVITY = [
  ['♥', '#ff90e8', 'Cool Dude donated $10'],
  ['★', '#a970ff', 'ninjadude subscribed · 3 mo'],
  ['＋', '#22c55e', 'new_viewer99 followed'],
  ['⚑', '#a970ff', 'bigstreamer raided · 412'],
  ['◆', '#22d3ee', 'richquick cheered 500 bits'],
  ['$', '#ff4d4d', 'ytfan Super Chat · $20'],
  ['★', '#ee1d52', 'tiktoklover sent Galaxy ×3'],
  ['♥', '#ff90e8', 'mvp_mike donated $5'],
];
const ACTIVITY_POOL = [
  ['♥', '#ff90e8', 'sunny_v donated $15'],
  ['★', '#a970ff', 'kombatkid subscribed · 5 mo'],
  ['＋', '#22c55e', 'lurker_jane followed'],
  ['◆', '#22d3ee', 'bitlord cheered 1,000 bits'],
  ['$', '#ff4d4d', 'ytwhale Super Chat · $50'],
  ['★', '#ee1d52', 'tt_fan sent Rocket ×2'],
  ['⚑', '#a970ff', 'partner_pat raided · 850'],
  ['♥', '#ff90e8', 'gigglepix donated $8'],
];
const STAMP_LADDER = ['now', '15s', '42s', '1m', '2m', '4m', '6m', '9m'];
const ACT_VISIBLE = 8;
const ACT_STEP_MS = 4500;

const rand = (lo, hi) => lo + Math.floor(Math.random() * (hi - lo + 1));
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const fmtK = (chars) => `${(chars / 1000).toFixed(1)}K`;
const fmtUptime = (totalSec) => {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};
// Looping chat feed for the live-stream demo (first CC_VISIBLE = the initial on-screen window).
const CHAT_POOL = [
  { name: 'pixelpanda', color: '#a970ff', text: 'first time catching you live lets gooo' },
  { name: 'mochi_uwu', color: '#ee1d52', text: 'the grumpy cat is everything 😂' },
  { sound: 'airhorn', name: 'BigTipper' },
  { name: 'streamsniped', color: '#ff6b6b', text: 'whats your sensitivity?' },
  { name: 'ggwarrior', color: '#7dd3fc', text: 'that clutch was insane, how are you this cracked??' },
  { name: 'nightowl', color: '#22c55e', text: 'been lurking all stream, finally saying hi 👋' },
  { name: 'retro_raccoon', color: '#fbbf24', text: 'what game is next?' },
  { sound: 'tada', name: 'queenbee' },
  { name: 'vibecheck', color: '#c084fc', text: 'the TTS reading this out loud is sending me lmao' },
  { name: 'lofi_lurker', color: '#7dd3fc', text: 'your overlay is so clean, what do you use?' },
];
const CC_VISIBLE = 5; // chat messages kept on screen
const CC_STEP_MS = 2600; // new-message cadence

function SpeakerIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="#ff90e8" aria-hidden="true" className="shrink-0">
      <path d="M3 10v4h4l5 5V5L7 10H3z" />
    </svg>
  );
}

// One square stream-deck tile at mock scale — mirrors quick-action-tile.js
// faces: emoji over label; toggles carry the state dot (emerald glow ON /
// gray OFF) + the explicit ON/OFF word; the mode tile shows the active
// layout with its ⇄ hint; active faces get the pink border + pink/10 fill;
// the question tile's count badge rides the corner.
function DeckTile({ icon, label, kind = 'fire', on = false, badge = null }) {
  const active = (kind === 'toggle' && on) || kind === 'mode';
  return (
    <span
      className={`relative flex aspect-square w-full select-none flex-col items-center justify-center gap-0.5 rounded-lg border px-0.5 text-center ${
        active ? 'border-gum-pink bg-gum-pink/10' : 'border-gum-gray-700 bg-gum-gray-900'
      }`}
    >
      {kind === 'toggle' && (
        <span
          aria-hidden="true"
          className={`absolute right-1 top-1 h-1 w-1 rounded-full ${
            on ? 'bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.6)]' : 'bg-gum-gray-600'
          }`}
        />
      )}
      {badge != null && (
        <span className="absolute right-1 top-0.5 font-mono text-[7px] font-bold text-gum-pink">{badge}</span>
      )}
      <span aria-hidden="true" className="text-[13px] leading-none">
        {icon}
      </span>
      <span className="text-[6px] font-semibold leading-tight text-gum-gray-300">{label}</span>
      {kind === 'toggle' && (
        <span className={`text-[5.5px] font-bold leading-none ${on ? 'text-emerald-400' : 'text-gum-gray-500'}`}>
          {on ? 'ON' : 'OFF'}
        </span>
      )}
      {kind === 'mode' && (
        <span className="whitespace-nowrap text-[5px] leading-none text-gum-gray-500">⇄ tap to switch</span>
      )}
    </span>
  );
}

function DashboardMock() {
  const [messages, setMessages] = useState(() => CHAT_POOL.slice(0, CC_VISIBLE).map((m, i) => ({ ...m, id: i })));
  const [readingId, setReadingId] = useState(CC_VISIBLE - 1);
  const [nowPlaying, setNowPlaying] = useState({
    name: CHAT_POOL[CC_VISIBLE - 1].name,
    text: CHAT_POOL[CC_VISIBLE - 1].text,
  });
  const [activity, setActivity] = useState(() =>
    DASH_ACTIVITY.map((e, i) => ({ glyph: e[0], color: e[1], text: e[2], id: i })),
  );
  const [viewers, setViewers] = useState({ total: 2532, twitch: 1284, youtube: 342, tiktok: 906 });
  const [chatPerMin, setChatPerMin] = useState(84);
  const [queue, setQueue] = useState(2);
  const [uptime, setUptime] = useState(10029);
  const [ttsToday, setTtsToday] = useState(41600);
  const [sessionTotal, setSessionTotal] = useState(12400);
  const [sessionAlerts, setSessionAlerts] = useState(7);
  const [sessionSounds, setSessionSounds] = useState(23);

  useEffect(() => {
    // Static busy snapshot for reduced-motion users — no live streaming.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Chat stream + the TTS / sound counters it drives.
    let poolIdx = CC_VISIBLE;
    let nextId = CC_VISIBLE;
    const chatTimer = setInterval(() => {
      const msg = { ...CHAT_POOL[poolIdx % CHAT_POOL.length], id: nextId };
      poolIdx += 1;
      nextId += 1;
      setMessages((prev) => [...prev, msg].slice(-CC_VISIBLE));
      if (msg.sound) {
        setSessionSounds((n) => n + 1);
      } else {
        setReadingId(msg.id);
        setNowPlaying({ name: msg.name, text: msg.text });
        setTtsToday((n) => n + msg.text.length + 30);
        setSessionTotal((n) => n + msg.text.length + 30);
      }
    }, CC_STEP_MS);

    // Activity feed + the session Alerts counter it drives.
    let actIdx = 0;
    let actId = DASH_ACTIVITY.length;
    const actTimer = setInterval(() => {
      const e = ACTIVITY_POOL[actIdx % ACTIVITY_POOL.length];
      actIdx += 1;
      const item = { glyph: e[0], color: e[1], text: e[2], id: actId };
      actId += 1;
      setActivity((prev) => [item, ...prev].slice(0, ACT_VISIBLE));
      setSessionAlerts((n) => n + 1);
    }, ACT_STEP_MS);

    // Live-count jitter (total always = the three platforms summed).
    const statTimer = setInterval(() => {
      setViewers((v) => {
        const twitch = clamp(v.twitch + rand(-3, 7), 1100, 1500);
        const youtube = clamp(v.youtube + rand(-2, 4), 250, 480);
        const tiktok = clamp(v.tiktok + rand(-3, 6), 750, 1100);
        return { twitch, youtube, tiktok, total: twitch + youtube + tiktok };
      });
      setChatPerMin((c) => clamp(c + rand(-4, 5), 64, 120));
      setQueue(rand(1, 4));
    }, 3000);

    // Uptime clock.
    const upTimer = setInterval(() => setUptime((u) => u + 1), 1000);

    return () => {
      clearInterval(chatTimer);
      clearInterval(actTimer);
      clearInterval(statTimer);
      clearInterval(upTimer);
    };
  }, []);

  return (
    <div className="mx-auto max-w-[860px] rounded-2xl border border-gum-pink/30 bg-gum-pink/[0.04] p-2 sm:p-3 shadow-[0_30px_90px_-40px_rgba(255,144,232,0.45)]">
      <div className="overflow-x-auto rounded-xl border border-gum-gray-800 bg-[#0a0a0a]">
        <div className="min-w-[720px]">
          {/* TOP BAR */}
          <div className="flex items-center justify-between border-b border-gum-gray-800 px-3 py-1.5">
            <div className="flex items-center gap-2 text-[9px]">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9ca3af"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M3 6h18M3 12h18M3 18h18" strokeLinecap="round" />
              </svg>
              <span className="flex items-center gap-1 text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Connected
              </span>
            </div>
            <span className="text-sm font-black italic tracking-[0.12em] text-gum-pink">SHOOSH</span>
            <span className="rounded border border-red-500/60 px-2 py-0.5 text-[9px] font-bold text-red-400">
              Stop Shoosh
            </span>
          </div>

          {/* TOOLBAR */}
          <div className="flex items-center gap-1.5 border-b border-gum-gray-800 px-3 py-1.5 text-[9px] text-gum-gray-400">
            <span className="whitespace-nowrap">Queue: 2</span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: '#9146ff' }} />
              Twitch
            </span>
            <span className="mx-0.5 h-3 w-px bg-gum-gray-800" />
            <DBtn>Skip</DBtn>
            <DBtn>Pause</DBtn>
            <DBtn>Clear TTS</DBtn>
            <DBtn>Clear Chat</DBtn>
            <DBtn active>
              <span className="inline-flex items-center gap-1">
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  aria-hidden="true"
                >
                  <path
                    d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6"
                    strokeLinecap="round"
                  />
                </svg>
                Levels ▲
              </span>
            </DBtn>
            <span className="ml-auto flex items-center gap-1.5 text-gum-gray-500">
              <span>A-</span>
              <span>A+</span>
              <span>⏱</span>
              <span className="flex h-4 w-4 items-center justify-center rounded-full border border-emerald-500/50 text-emerald-400">
                ↓
              </span>
            </span>
          </div>

          {/* BODY */}
          <div className="flex">
            {/* MAIN STAGE — live chat */}
            <div className="relative min-h-[330px] flex-1 overflow-hidden">
              {/* Levels popover (open) */}
              <div className="absolute left-3 top-2 z-10 w-44 rounded-lg border border-gum-gray-700 bg-[#111] p-2.5 shadow-2xl">
                <div className="mb-2 text-[7px] font-bold uppercase tracking-[0.2em] text-gum-gray-500">Mixer</div>
                <div className="space-y-2.5">
                  <MixerFader icon="🔊" label="TTS" sub="Chat read aloud" pct={41} />
                  <MixerFader icon="🎵" label="SFX" sub="Chat sound effects" pct={63} />
                  <MixerFader icon="🔔" label="Alerts" sub="Alert + announcement audio" pct={48} />
                </div>
                <button className="mt-2.5 w-full rounded-md border border-gum-gray-700 py-1.5 text-[8px] font-bold uppercase tracking-wider text-gum-gray-300 hover:border-red-500/50 hover:text-red-400">
                  ⏻ Mute all (panic)
                </button>
              </div>

              {/* chat, bottom-anchored */}
              <div className="absolute inset-x-0 bottom-0 flex flex-col justify-end gap-1 p-3">
                {messages.map((m) =>
                  m.sound ? (
                    <div
                      key={m.id}
                      className="cc-msg flex w-fit items-center gap-1.5 rounded border-l-2 border-gum-pink/70 bg-gum-pink/5 px-1.5 py-0.5 text-[10px] font-semibold text-gum-pink"
                    >
                      <span>🔊</span>
                      <span className="font-bold">{m.name}</span>
                      <span>played !{m.sound}</span>
                    </div>
                  ) : (
                    <div
                      key={m.id}
                      className={`cc-msg flex items-baseline gap-1.5 text-[10px] leading-snug ${
                        m.id === readingId
                          ? 'rounded border-l-2 border-gum-pink/70 bg-gum-pink/[0.06] px-1.5 py-0.5'
                          : ''
                      }`}
                    >
                      <span className="font-bold" style={{ color: m.color }}>
                        {m.name}
                      </span>
                      <span className="text-gum-gray-200">{m.text}</span>
                      {m.id === readingId && (
                        <span className="ml-1 inline-flex items-center gap-1 self-center">
                          <SpeakerIcon />
                          <WaveBars h={9} />
                        </span>
                      )}
                    </div>
                  ),
                )}
              </div>
            </div>

            {/* QUICK ACTIONS — the deck column, exactly where the real one
                lives (dashboard-client.js: its own w-44 rail between the
                stage and the status column) at mock scale: ⚡ header + ✎ edit
                pill over a 2-col grid of SQUARE stream-deck tiles
                (quick-action-tile.js faces). Labels hand-mirror real entries
                in lib/quick-action-catalog.js (no import — the mock must not
                pull dashboard runtime code into the landing bundle). */}
            <div className="w-[104px] shrink-0 border-l border-gum-gray-800 bg-[#0b0b0b] p-1.5">
              <div className="mb-1.5 flex items-center justify-between gap-1">
                <span className="whitespace-nowrap text-[6.5px] font-bold uppercase tracking-[0.12em] text-gum-pink">
                  ⚡ Quick actions
                </span>
                <span className="whitespace-nowrap rounded-full border border-gum-gray-700 px-1 py-px text-[6px] text-gum-gray-500">
                  ✎ edit
                </span>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <DeckTile icon="🤫" label="Shoosh" />
                <DeckTile icon="🔇" label="Mute all" kind="toggle" />
                <DeckTile icon="✨" label="Viewer FX" kind="toggle" on />
                <DeckTile icon="🎭" label="Heckle" kind="mode" />
                <DeckTile icon="🧟" label="Start siege" />
                <DeckTile icon="❓" label="Next question" badge={1} />
              </div>
            </div>

            {/* RIGHT RAIL */}
            <div className="w-[262px] shrink-0 space-y-1.5 border-l border-gum-gray-800 bg-[#0c0c0c] p-1.5">
              {/* LIVE */}
              <div className="rounded-lg border border-gum-gray-800 bg-[#0e0e0e] p-2">
                <RailHeader title="Live" />
                <div className="flex items-baseline gap-1.5">
                  <span className="font-mono text-base font-black text-white">
                    {viewers.total.toLocaleString('en-US')}
                  </span>
                  <span className="text-[8px] text-gum-gray-500">watching now</span>
                </div>
                <div className="my-1.5 h-px bg-gum-gray-800" />
                <div className="space-y-1">
                  {PLATFORM_META.map(([name, c, key]) => (
                    <div key={name} className="flex items-center justify-between text-[9px]">
                      <span className="flex items-center gap-1.5 text-gum-gray-300">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />
                        {name}
                      </span>
                      <span className="font-mono font-semibold text-gum-gray-200">
                        {viewers[key].toLocaleString('en-US')}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1">
                  <StatTile label="Uptime" value={fmtUptime(uptime)} />
                  <StatTile label="Queue" value={`${queue} waiting`} />
                  <StatTile label="Chat" value={`${chatPerMin} / min`} />
                  <StatTile label="TTS today" value={`${fmtK(ttsToday)} / 100K`} />
                </div>
                <div className="mt-2 text-[6.5px] font-bold uppercase tracking-wide text-gum-gray-600">
                  This session
                </div>
                <div className="mt-1 grid grid-cols-2 gap-1">
                  <div className="rounded border border-gum-gray-800 bg-black/40 px-1.5 py-1 text-center">
                    <div className="font-mono text-[11px] font-bold text-gum-pink">{sessionAlerts}</div>
                    <div className="text-[6.5px] uppercase tracking-wide text-gum-gray-600">Alerts</div>
                  </div>
                  <div className="rounded border border-gum-gray-800 bg-black/40 px-1.5 py-1 text-center">
                    <div className="font-mono text-[11px] font-bold text-gum-pink">{sessionSounds}</div>
                    <div className="text-[6.5px] uppercase tracking-wide text-gum-gray-600">Sounds</div>
                  </div>
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[6.5px] text-gum-gray-600">
                  <span>Total this session {fmtK(sessionTotal)}</span>
                  <span>Monthly limit: 500K</span>
                </div>
              </div>

              {/* NOW PLAYING — matches the live chat below */}
              <div className="rounded-lg border border-gum-gray-800 bg-[#0e0e0e] p-2">
                <RailHeader title="Now playing" />
                <div className="flex items-center gap-2 py-0.5">
                  <SpeakerIcon />
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] font-bold text-gum-gray-200">{nowPlaying.name}</div>
                    <div className="truncate text-[8px] text-gum-gray-500">{nowPlaying.text}</div>
                  </div>
                  <WaveBars h={11} />
                </div>
              </div>

              {/* ACTIVITY — varied events */}
              <div className="rounded-lg border border-gum-gray-800 bg-[#0e0e0e] p-2">
                <RailHeader title="Activity" />
                <div className="max-h-[118px] space-y-1 overflow-hidden">
                  {activity.map((e, idx) => (
                    <div key={e.id} className="cc-drop flex items-center gap-1.5 text-[9px]">
                      <span
                        className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-[9px]"
                        style={{ background: `${e.color}22`, color: e.color }}
                      >
                        {e.glyph}
                      </span>
                      <span className="truncate text-gum-gray-300">{e.text}</span>
                      <span className="ml-auto text-[7px] text-gum-gray-700">{STAMP_LADDER[idx] ?? ''}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* QUESTIONS */}
              <div className="rounded-lg border border-gum-gray-800 bg-[#0e0e0e] p-2">
                <div className="flex items-center gap-1.5 text-[8px] font-bold uppercase tracking-[0.12em] text-gum-gray-200">
                  🤔 Questions <span className="text-gum-pink">1</span>
                </div>
                <p className="mt-1.5 text-[8px] leading-relaxed text-gum-gray-500">
                  <span className="font-semibold text-gum-gray-300">curiouscat</span> asks{' '}
                  <span className="text-gum-gray-400">“what’s your favorite song?”</span> — click to read.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CommandCenter() {
  return (
    <section id="command-center" className="px-6 py-24">
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-12 reveal">
          <Eyebrow>The command center</Eyebrow>
          <h2 className="mt-5 text-4xl md:text-6xl font-black tracking-tight leading-[1.05]">
            StreamElements ran in the background.
            <br />
            <span className="gradient-text">Shoosh runs the show.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-gum-gray-400 leading-relaxed">
            Every other tool hides in a settings page you open once and forget. Shoosh is a cockpit you keep open and
            watch all stream — levels, live viewers across all three platforms, every event as it lands, and a one-tap
            quick-actions deck for your mid-stream moves.
          </p>
        </div>
        <div className="reveal" data-delay="150">
          <DashboardMock />
        </div>
      </div>
    </section>
  );
}
